import { ButtonHTMLAttributes, forwardRef, ReactNode } from "react";

export type ButtonVariant = "primary-dark" | "primary-yellow" | "outline-dark" | "outline-light";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant: ButtonVariant;
  trailingIcon?: ReactNode;
  leadingIcon?: ReactNode;
}

const VARIANT_CLASS: Record<ButtonVariant, string> = {
  "primary-dark": "bg-[#0D0D0D] text-white hover:bg-[#1f1f1f]",
  "primary-yellow": "bg-[#FFD100] text-[#0D0D0D] hover:bg-[#EEC300]",
  "outline-dark": "bg-transparent text-[#0D0D0D] border border-[#0D0D0D] hover:bg-[#0D0D0D]/5",
  "outline-light": "bg-transparent text-white border border-white hover:bg-white/10",
};

const BASE_CLASS =
  "inline-flex h-14 items-center justify-center gap-2 rounded-full px-8 py-4 " +
  "whitespace-nowrap text-base [font-weight:600] leading-relaxed transition-colors " +
  "disabled:cursor-not-allowed disabled:opacity-50 " +
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#0D0D0D]";

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ variant, leadingIcon, trailingIcon, children, className, type = "button", ...rest }, ref) => {
    const composedClass = [BASE_CLASS, VARIANT_CLASS[variant], className].filter(Boolean).join(" ");

    return (
      <button ref={ref} type={type} className={composedClass} {...rest}>
        {leadingIcon}
        <span>{children}</span>
        {trailingIcon}
      </button>
    );
  }
);

Button.displayName = "Button";
